// The customer live socket (VisionPlatform contracts/asyncapi/live.yaml 0.4.0, WEB-F-RND-04): one per session,
// signed in with `hello`, kept signed in with `auth`, and subscriptions sent again after every reconnect.
// Framework-free; LiveProvider connects it to React and the query cache.
import type { components } from "../api/generated/vision-api";

type AlarmItem = components["schemas"]["AlarmItem"];

export type Target =
  | { device: string }
  | { group: string }
  | { space: string }
  | { dashboard: string }
  /** The alarms of every device the user can view in the organisation (live 0.3.0). */
  | { alarms: true }
  /** The server automations the user may see in the organisation (live 0.4.0). */
  | { automations: true };

export type LatestMessage = {
  type: "latest";
  sub: string;
  device: string;
  values: Record<string, { value: unknown; at: string; backfilled?: boolean }>;
};
export type DeviceStateMessage = {
  type: "device_state";
  sub: string;
  device: string;
  status: "online" | "sleeping" | "offline" | "never_seen";
  last_seen_at?: string | null;
  /** The device's own latest position when it changed (live 0.2.0, vision-api Device.position). */
  position?: { lat: number; lon: number; at: string };
};
/** An alarm was raised, acknowledged or cleared (live 0.3.0, vision-api AlarmItem 0.26.0). */
export type AlarmMessage = {
  type: "alarm";
  sub: string;
  event: "raised" | "acknowledged" | "cleared";
  alarm: AlarmItem;
};
/** A server automation ran, failed or was paused (live 0.4.0, as vision-api AutomationItem 0.55.0). */
export type AutomationMessage = {
  type: "automation";
  sub: string;
  event: "ran" | "failed" | "paused";
  automation: {
    id: string;
    name: string;
    enabled: boolean;
    last_ran_at: string | null;
    last_result?: string | null;
    paused_reason?: string | null;
  };
};
type Incoming =
  | { type: "ready"; user_id: string; org_id?: string | null; expires_at: string }
  | { type: "subscribed"; sub: string; devices?: string[] }
  | { type: "refused" | "ended"; sub: string; code: string; detail?: string }
  | LatestMessage
  | DeviceStateMessage
  | AlarmMessage
  | AutomationMessage;

export type LiveStatus = "connecting" | "ready" | "closed";

export interface LiveOptions {
  url: string;
  /** The current access token of the customers realm, or undefined when signed out. */
  token: () => Promise<string | undefined>;
  orgId?: string;
  onLatest: (m: LatestMessage) => void;
  onDeviceState: (m: DeviceStateMessage) => void;
  onAlarm?: (m: AlarmMessage) => void;
  onAutomation?: (m: AutomationMessage) => void;
  onStatus?: (s: LiveStatus) => void;
  /**
   * The server will not send a target's data (WEB-F-05): `refused` at subscribe time, or `ended` when the user lost
   * `view` on it. The screens showing it keep polling. `subscribed` (again, after a reconnect) undoes it.
   */
  onFollowing?: (target: Target, following: boolean) => void;
  /** 4401 with reason session_ended: a sign out everywhere ended this session (vision-api 0.9.0). */
  onSessionEnded?: () => void;
  /** For tests. */
  WebSocketImpl?: typeof WebSocket;
  random?: () => number;
}

/** Back-off before reconnect attempt n (from 0): 1 s doubling to 30 s, each ±20 % so tabs do not reconnect at once. */
export function backoffMs(attempt: number, random = Math.random): number {
  const base = Math.min(30_000, 1000 * 2 ** attempt);
  return Math.round(base * (0.8 + 0.4 * random()));
}

// Codes after which reconnecting cannot help: a malformed message or the wrong realm is a bug, not a blip.
const FINAL = new Set([4400, 4403]);
/** Send `auth` this long before the token expires. */
const RENEW_BEFORE_MS = 30_000;

export class LiveSocket {
  private ws: WebSocket | undefined;
  private subs = new Map<string, { target: Target; count: number }>();
  private attempt = 0;
  private retry: ReturnType<typeof setTimeout> | undefined;
  private renew: ReturnType<typeof setTimeout> | undefined;
  private stopped = false;
  private ready = false;
  private opening = false;
  private waitingForToken = false;

  constructor(private readonly o: LiveOptions) {}

  start(): void {
    this.stopped = false;
    void this.open();
  }

  stop(): void {
    this.stopped = true;
    clearTimeout(this.retry);
    clearTimeout(this.renew);
    this.ws?.close(1000);
    this.ws = undefined;
    this.setStatus("closed");
  }

  /** Follows a target until the returned function is called; the same target is subscribed once, counted. */
  subscribe(target: Target): () => void {
    // The sub id names the target ("device:<uuid>"), at most 46 characters (SubId allows 64).
    const [kind, id] = Object.entries(target)[0] ?? ["", ""];
    const key = `${kind}:${String(id)}`;
    let entry = this.subs.get(key);
    if (!entry) {
      entry = { target, count: 0 };
      this.subs.set(key, entry);
    }
    entry.count++;
    if (entry.count === 1 && this.ready) this.send({ type: "subscribe", sub: key, target });
    return () => {
      const e = this.subs.get(key);
      if (!e) return;
      e.count--;
      if (e.count > 0) return;
      this.subs.delete(key);
      if (this.ready) this.send({ type: "unsubscribe", sub: key });
    };
  }

  /** A fresh token, as soon as the app has one (oidc-client-ts renews before expiry). */
  refreshed(token: string): void {
    if (this.ready) this.send({ type: "auth", token });
    // Closed for want of a token (expired during sleep, or a 4401 at expiry before the renewal): connect now.
    else if (this.waitingForToken && !this.stopped) {
      clearTimeout(this.retry);
      void this.open();
    }
  }

  private async open(): Promise<void> {
    if (this.opening) return;
    this.opening = true;
    const token = await this.o.token().catch(() => undefined);
    this.opening = false;
    if (this.stopped) return;
    if (!token) {
      // No token for now: try again with back-off, or as soon as a renewal brings one (refreshed). Giving up here
      // left the screens polling until a reload.
      this.waitingForToken = true;
      this.setStatus("closed");
      clearTimeout(this.retry);
      this.retry = setTimeout(() => void this.open(), backoffMs(this.attempt++, this.o.random));
      return;
    }
    this.waitingForToken = false;
    this.setStatus("connecting");
    const WS = this.o.WebSocketImpl ?? WebSocket;
    const ws = new WS(this.o.url);
    this.ws = ws;
    ws.onopen = () => this.send({ type: "hello", token, ...(this.o.orgId ? { org_id: this.o.orgId } : {}) });
    ws.onmessage = (e) => this.receive(e.data);
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = undefined;
      this.ready = false;
      clearTimeout(this.renew);
      this.setStatus("closed");
      if (this.stopped) return;
      if (e.code === 4401 && e.reason === "session_ended") {
        this.stopped = true;
        this.o.onSessionEnded?.();
        return;
      }
      if (FINAL.has(e.code)) return;
      // 4401 (token expired), 4429, 1001 and network loss: back off, sign in again, resubscribe.
      this.retry = setTimeout(() => void this.open(), backoffMs(this.attempt++, this.o.random));
    };
  }

  private receive(data: unknown): void {
    let m: Incoming;
    try {
      m = JSON.parse(String(data)) as Incoming;
    } catch {
      return;
    }
    switch (m.type) {
      case "ready": {
        this.ready = true;
        this.attempt = 0;
        this.setStatus("ready");
        for (const [sub, { target }] of this.subs) this.send({ type: "subscribe", sub, target });
        const wait = Date.parse(m.expires_at) - Date.now() - RENEW_BEFORE_MS;
        clearTimeout(this.renew);
        if (Number.isFinite(wait))
          this.renew = setTimeout(
            () =>
              void this.o.token().then((t) => {
                if (t) this.refreshed(t);
              }),
            Math.max(0, wait),
          );
        return;
      }
      case "latest":
        if (this.subs.has(m.sub)) this.o.onLatest(m);
        return;
      case "device_state":
        if (this.subs.has(m.sub)) this.o.onDeviceState(m);
        return;
      case "alarm":
        if (this.subs.has(m.sub)) this.o.onAlarm?.(m);
        return;
      case "automation":
        if (this.subs.has(m.sub)) this.o.onAutomation?.(m);
        return;
      case "subscribed":
      case "refused":
      case "ended": {
        const target = this.subs.get(m.sub)?.target;
        if (target) this.o.onFollowing?.(target, m.type === "subscribed");
        return;
      }
      default:
        return;
    }
  }

  private send(m: object): void {
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(m));
  }

  private setStatus(s: LiveStatus): void {
    this.o.onStatus?.(s);
  }
}
